/*
 * @Description: 实时定位驾驶舱 — 类型定义
 *   字段严格对齐《Web 实时定位驾驶舱 — 后端接口文档 v7.4》。
 *   覆盖 A1–A4 / B1·B3·B4 / C1 / H1，以及 v7.4「状态×原因」口径
 *   （offlineReason/offlineDesc + 物理休眠态）与 B3 休眠区段 sleepSegments。
 */

/** 通用响应信封（成功 + 失败均套此结构；code===200 即成功语义） */
export interface CockpitEnvelope<T> {
  code: number
  msg: string
  /** 空态/提示文案（如 1002 未绑定 / 1004 暂无数据时由后端给出） */
  toast_msg: string
  /** 业务数据；空态时为 null（非错误，前端按空处理） */
  data: T | null
}

/**
 * 离线原因机器码（v7.4 §4.9，服务端统一下发，前端只渲染对应 offlineDesc 文案，
 * 不自行按 sleepState 推断；判定顺序见文档）。
 */
export type OfflineReason =
  | 'no_device'
  | 'paused'
  | 'no_motion'
  | 'low_battery'
  | 'not_seen_since_bind'
  | 'no_signal'

/** 离线原因人数构成（offlineReason→人数；人数为 0 的 key 不出现） */
export type OfflineReasonStats = Partial<Record<OfflineReason, number>>

/**
 * 状态×原因公共片段（v7.4 §4.9）：A3 item / A4 详情共用。
 * 全部 omitempty——在线项不返 offline*，物理态 active 时不返 sleepTrigger/sleepSince。
 */
export interface OfflineReasonMeta {
  /** 离线原因机器码（仅 !online 返） */
  offlineReason?: OfflineReason
  /** 离线原因人话文案（原样展示，仅 !online 返；「休眠」字样不出现在管理端） */
  offlineDesc?: string
  /** 入睡来源（物理态透出，active 不返）：self 自休眠 / command 命令休眠 */
  sleepTrigger?: 'self' | 'command'
  /** 进入休眠时刻，unix 毫秒（物理态透出，active 不返） */
  sleepSince?: number
}

/** A3 单个成员的最新位置（对齐 /realtime/locations 的 items 项） */
export interface MemberLocationItem extends OfflineReasonMeta {
  userId: string
  name: string
  avatar: string
  online: boolean
  /** "lng,lat" GCJ02 字符串，用 parseLngLat 解析 */
  location: string
  /** 定位时间，unix 毫秒 */
  locateTime: number
  /** A3（ListDevices）不返回地址，需前端/BFF 调 regeo 补，故可选 */
  address?: string
  /** 在线设备的细分状态，如 moving / staying / offline_last */
  locationState: string
  /** 细分状态的中文展示，如 "移动中"（离线时为空） */
  locationStateLabel: string
  /** 物理休眠态（active / light / deep 等，透出调试用，不直接展示文案） */
  sleepState: string
  battery: number
}

/** A3 响应 data（/realtime/locations） */
export interface RealtimeLocationsData {
  total: number
  /** 全部成员数（含离线） */
  allCnt?: number
  onlineCnt: number
  offlineCnt: number
  /** 离线原因人数构成（v7.4，同 A1.offlineDetail 口径） */
  offlineReasonStats?: OfflineReasonStats
  items: MemberLocationItem[]
}

/** A1 实时态状态统计（date=当天） */
export interface RealtimeSummaryRealtime {
  isRealtime: true
  /** ListDevices.allCnt（有定位数据成员，= online + offline，与分桶同源） */
  totalMembers: number
  online: number
  moving: number
  staying: number
  offline: number
  /** 离线原因构成（v7.4，offlineReason→人数，离线数字下的小字摊开用） */
  offlineDetail?: OfflineReasonStats
  /** 当日未结、排除 recovered 的去重异常【成员数】（非告警条数） */
  abnormal: number
  /** 当日首个轨迹点时间，unix 毫秒（当前恒 0 降级，前端用 timeline.startLocation.time 兜底） */
  firstReportTime: number
}

/** A1 历史态状态统计（date≠当天）：仅当日活跃 */
export interface RealtimeSummaryHistory {
  isRealtime: false
  totalMembers: number
  /** 当日有轨迹点的成员去重数 */
  activeToday: number
}

/** A1 响应 data（/realtime/summary）：实时态 / 历史态二选一，由 isRealtime 区分 */
export type RealtimeSummaryData =
  | RealtimeSummaryRealtime
  | RealtimeSummaryHistory

/** A2 成员列表项（对齐 /realtime/members 的 list 项） */
export interface RealtimeMemberItem {
  userId: string
  name: string
  avatar: string
  /** 部门路径，如 "物业部 · 巡检一组" */
  department: string
  imei: string
  deviceNo: string
  online: boolean
  battery: number
  signal: number
  gpsStatus: number
  lastOnlineAt: number
  /** "lng,lat" GCJ02 */
  location: string
  locateTime: number
  address: string
  /** 是否已分配（绑定管理口径） */
  assigned?: boolean
  /** 历史是否曾绑定过设备 */
  hasBoundBefore?: boolean
}

/** A2 状态汇总 */
export interface RealtimeMembersStateStats {
  online: number
  offline: number
  lowBattery: number
}

/** A2 响应 data（/realtime/members，ClassifyMembers 绑定管理口径，分页） */
export interface RealtimeMembersData {
  total: number
  /** 已分配 / 未分配数（v7.4 文档明列） */
  assignedCnt?: number
  unassignedCnt?: number
  pageNo?: number
  pageSize?: number
  stateStats: RealtimeMembersStateStats
  items: RealtimeMemberItem[]
}

/** A4 成员详情（/member/detail，扁平对象） */
export interface MemberDetail extends OfflineReasonMeta {
  userId: string
  name: string
  avatar: string
  online: boolean
  battery: number
  signal: number
  gpsStatus: number
  /** 物理休眠态（透出调试用，不直接展示文案） */
  sleepState: string
  /** "lng,lat" GCJ02 */
  location: string
  address?: string
  locateTime: number
  imei: string
  deviceNo: string
  usageDays: number
  bindAt: number
  lastOnlineAt: number
  bound: boolean
}

/** C1 动态卡事件类型（对齐接口文档 events.feed 的 type 枚举；v7.4 新增 deep_sleep） */
export type CockpitEventType =
  | 'photo'
  | 'stay_timeout'
  | 'continuous_distance'
  | 'offline'
  | 'online'
  | 'deep_sleep'

/** C1 事件流单项（两路合并：拍照 evidence + 告警 alert） */
export interface CockpitEventItem {
  /** 事件时间，unix 毫秒 */
  time: number
  type: CockpitEventType
  userId: string
  userName: string
  avatar: string
  /** 事件描述，如 "拍照 10 张" / "停留超过 30 分钟" */
  desc: string
  address: string
  /** "lng,lat" GCJ02 */
  location: string
  /** 拍照事件的缩略图（最多展示前 3 张） */
  thumbnails?: string[]
  /** 拍照事件的总张数 */
  photoCount?: number
  /** 告警级别，如 info / warn / critical */
  level?: string
}

/** C1 响应 data（/events/feed），时间倒序 */
export interface EventsFeedData {
  total: number
  items: CockpitEventItem[]
}

/** 告警动态流类型（C1' /alerts/feed 的 ruleType；all=全部，online→recovered） */
export type AlertFeedType =
  | 'offline'
  | 'online'
  | 'stay_timeout'
  | 'continuous_distance'
  | 'deep_sleep'
  | 'low_battery'
  | 'ai_anomaly'

/** 告警动态流单项（C1' /alerts/feed，卡片结构同 C1：time/type/用户/desc/level/location） */
export interface AlertFeedItem {
  /** 告警时间，unix 毫秒 */
  time: number
  type: AlertFeedType
  userId: string
  userName: string
  avatar: string
  /** 告警内容文案（后端下发，原样展示） */
  desc: string
  /** 告警级别：warn / info */
  level?: string
  /** "lng,lat" GCJ02（部分告警无位置） */
  location?: string
}

/** 告警动态流响应 data（C1' /alerts/feed），时间倒序 */
export interface AlertFeedData {
  total: number
  items: AlertFeedItem[]
}

/** 告警未读数响应 data（D5 /alerts/unread-count，未读 = read_at IS NULL） */
export interface AlertUnreadCountData {
  count: number
}

/** 告警规则类型（D 节 7 类；recovered=重新上线 info） */
export type AlertRuleType =
  | 'low_battery'
  | 'offline'
  | 'recovered'
  | 'continuous_distance'
  | 'stay_timeout'
  | 'ai_anomaly'
  | 'deep_sleep'

/** 告警记录单项（D1 /alerts/list，透传 ListAlerts） */
export interface AlertListItem {
  id: number
  groupId?: string
  userId: string
  userName: string
  imei?: string
  ruleType: AlertRuleType
  /** info / warn / critical */
  level?: string
  title: string
  content: string
  /** "lng,lat" GCJ02（部分告警无位置） */
  location?: string
  /** 触发时刻，unix 毫秒 */
  triggeredAt: number
  /** 解除时刻，unix 毫秒；null = 未解除 */
  resolvedAt: number | null
  read?: boolean
}

/** 告警记录响应 data（D1 /alerts/list） */
export interface AlertListData {
  total: number
  pageNo: number
  pageSize: number
  hasUnread?: boolean
  items: AlertListItem[]
}

/** B1 单个轨迹点 */
export interface TrackPoint {
  /** "lng,lat" GCJ02 */
  location: string
  /** 定位时间，unix 毫秒 */
  locateTime: number
  speed?: number
  direction?: number
}

/** B1 单个成员的轨迹 */
export interface TeamTrackMember {
  userId: string
  name: string
  avatar: string
  /** 按时间升序的轨迹点 */
  points: TrackPoint[]
}

/** B1 响应 data（/track/team），≤24h */
export interface TeamTrackData {
  members: TeamTrackMember[]
}

/** B3 照片项 */
export interface TimelinePhoto {
  photoId?: number
  smallUrl: string
  largeUrl?: string
  time?: number
  lng?: number
  lat?: number
  address?: string
  mediaType?: number
}

/** B3 轨迹位置点（数值经纬度，非 location 字符串） */
export interface TimelineGeoPoint {
  time: number
  address?: string
  lng: number
  lat: number
  updatedSecAgo?: number
}

/** B3 timeline 事件（按 type 区分；move 段才有起止/距离/lineType/points） */
export type TimelineEvent =
  | { type: 'start'; time: number; lng: number; lat: number; address?: string }
  | {
      type: 'current'
      time: number
      lng: number
      lat: number
      address?: string
      updatedSecAgo?: number
    }
  | {
      type: 'stay'
      time: number
      lng: number
      lat: number
      address?: string
      stayDurationSec: number
      photoCount?: number
      photos?: TimelinePhoto[]
    }
  | {
      type: 'move'
      startTime: number
      endTime: number
      durationSec?: number
      distanceM?: number
      lineType: 'solid' | 'dashed'
      gapDurationSec?: number
      points?: TrackPoint[]
    }
  | {
      type: 'photo'
      time: number
      lng: number
      lat: number
      address?: string
      photos?: TimelinePhoto[]
    }

/** B3 timeline 汇总 */
export interface TimelineSummary {
  totalDistanceMeters: number
  totalDurationSec: number
  stayPointCount: number
  photoCount: number
  pointCount: number
}

/**
 * B3 休眠区段（v7.4，omitempty）：当日休眠时段，独立数组不混 events[]。
 * 渲染口径（§4.9）：与 dashed 离线段同一视觉族（"带原因的不可见时段"），
 * 文案按 sleepTrigger——command=「已暂停定位」、self=「工卡没动静」，不出现"休眠"字样。
 */
export interface SleepSegment {
  /** 起始时刻，unix 毫秒 */
  startMs: number
  /** 结束时刻，unix 毫秒（仍在休眠时 = now 且 ongoing=true） */
  endMs: number
  /** light 浅休眠 / deep 深休眠 */
  sleepType: 'light' | 'deep'
  /** 入睡来源：self 自休眠 / command 命令休眠 */
  sleepTrigger: 'self' | 'command'
  /** 唤醒来源：self / command / inferred；空 = ongoing 仍在休眠 */
  wakeTrigger?: 'self' | 'command' | 'inferred'
  /** 是否仍在进行中 */
  ongoing?: boolean
}

/** B3 响应 data（/track/timeline，单日，APP 同款） */
export interface TimelineData {
  summary: TimelineSummary
  startLocation?: TimelineGeoPoint
  currentLocation?: TimelineGeoPoint
  onDuty?: TimelineGeoPoint
  offDuty?: TimelineGeoPoint
  events: TimelineEvent[]
  /** 当日休眠区段（v7.4，omitempty，无休眠不返） */
  sleepSegments?: SleepSegment[]
  photos?: TimelinePhoto[]
}

/** B4 停留点 */
export interface TrackStayPoint {
  /** "lng,lat" GCJ02 */
  location: string
  startTime: number
  endTime: number
  durationSec: number
  address?: string
}

/** B4 响应 data（/track/stats）：回放页 4 数据格里程只认此（float，剔停留） */
export interface TrackStatsData {
  totalDistanceMeters: number
  totalDurationSec: number
  pointCount: number
  photoCount: number
  startLocation?: string
  endLocation?: string
  stayPoints?: TrackStayPoint[]
}

/** 经纬度数值对 */
export interface LngLat {
  lng: number
  lat: number
}

/**
 * 解析 "lng,lat" 坐标字符串为数值对。
 * 在系统边界（接口返回的字符串坐标）统一解析，内部代码直接用数值。
 *
 * @param location - 形如 "113.841,22.725" 的 GCJ02 坐标串
 * @returns 解析成功返回 { lng, lat }；为空 / 非法返回 null
 *
 * @example
 * parseLngLat('113.841,22.725') // { lng: 113.841, lat: 22.725 }
 * parseLngLat('') // null
 */
export function parseLngLat(location: string): LngLat | null {
  if (!location) return null
  const parts = location.split(',')
  if (parts.length !== 2) return null
  const lng = Number(parts[0])
  const lat = Number(parts[1])
  if (Number.isNaN(lng) || Number.isNaN(lat)) return null
  return { lng, lat }
}
